import { convertStringDateToUnixTimestamp } from '@utils/ConvertStringDateToUnixTimestamp';
import { PageType } from '@utils/pageConfig/Types';
import { format } from 'date-fns';
import { formatKpi, Kpi } from '@utils/Formatters';
import { Periodicity } from '@components/time-period-selector/PeriodsTypes';
import {
  ProductsLineChartResp,
  SaleTypeMarketResp,
  SaleTypeRetailerResp,
  SaleTypeRetailerShareResp,
} from '@components/products-line-charts/Types';
import { ChartKeys } from '@const/ChartKeys';
import { RetailerKpiOption, SaleType } from '@models/Products';

const marketKpiType = {
  [SaleType.revenue]: 'Market Revenue',
  [SaleType.volume]: 'Market Volume',
  [SaleType.price]: 'Market Price',
};

const retailerKpiShareType = {
  [SaleType.revenue]: 'My Revenue Share',
  [SaleType.volume]: 'My Volume Share',
  [SaleType.price]: 'My Price',
};

const retailerKpiMixType = {
  [SaleType.revenue]: 'My Revenue Mix',
  [SaleType.volume]: 'My Volume Mix',
  [SaleType.price]: 'My Price',
};

const retailerKpiAbsoluteType = {
  [SaleType.revenue]: 'My Revenue',
  [SaleType.volume]: 'My Volume',
  [SaleType.price]: 'My Price',
};

export const formatDate = (date, periodicity) =>
  periodicity === Periodicity.Weekly ? `Week ${format(date, 'w')}, ${format(date, 'yyyy')}` : format(date, 'MMM yy');

export const mapChartData = (data, convertPeriod, secondBaseChartResp = []) => {
  return data?.map(({ period, value }) => {
    const baseObject = {
      period: convertPeriod(period),
      value,
    };
    if (secondBaseChartResp.length > 0) {
      const matchingSecondBase = secondBaseChartResp.find((secondItem) => secondItem.period === period);

      if (matchingSecondBase) {
        baseObject.secondKpi = matchingSecondBase.value;
      }
    }

    return baseObject;
  });
};

export const limitTop10Items = (kpiType, kpiDataDto) => {
  const filteredKpiData = kpiDataDto
    .filter(({ brandName }) => brandName !== 'Total')
    .map(({ brandName, kpiData }) => {
      const kpi = kpiData.find(({ kpiType: type }) => type === kpiType);
      return kpi ? { brandName, value: kpi.marketAverage.at(-1).value } : null;
    })
    .filter(Boolean);

  const top10Items = filteredKpiData
    .sort((a, b) => b.value - a.value || a.brandName.localeCompare(b.brandName))
    .slice(0, 10)
    .map(({ brandName }) => brandName);

  top10Items.unshift('Total');

  return top10Items;
};
/*
 * Products page market and line chart
 * */

export const processProductsMarketLineChartData = (inputChartData, selectedSaleType) => {
  const existingNames = new Set();
  return inputChartData?.items?.map(({ item, rollingSalesDto }) => {
      const dto = Object.assign(new ProductsLineChartResp(), rollingSalesDto);
      const baseChartResp = SaleTypeMarketResp[selectedSaleType](dto);
      let groupName = `${item.itemBrand} ${item.itemName}`;
      while (existingNames.has(groupName)) {
        groupName += ' ';
      }
      existingNames.add(groupName);
      return { data: baseChartResp, groupName };
    })
    .map(({ data: marketChartData, groupName }) => ({
      groupName,
      data: mapChartData(marketChartData, convertStringDateToUnixTimestamp),
    }));
};

export const processProductsRetailerLineChartData = (inputData, selectedSaleType, selectedRetailer) => {
  const existingNames = new Set();
  return inputData?.items?.map(({ item, rollingSalesDto }) => {
      if (!rollingSalesDto) return [];
      const baseChartResp = SaleTypeRetailerShareResp[selectedSaleType](selectedRetailer)(rollingSalesDto);
      const secondBaseChartResp = SaleTypeRetailerResp[selectedSaleType](rollingSalesDto);
      let groupName = `${item.itemBrand} ${item.itemName}`;
      while (existingNames.has(groupName)) {
        groupName += ' ';
      }
      existingNames.add(groupName);
      return { data: baseChartResp, groupName, secondBaseChartResp };
    })
    .map(({ data: chartData, groupName, secondBaseChartResp }) => {
      return {
        groupName,
        data: mapChartData(chartData, convertStringDateToUnixTimestamp, secondBaseChartResp),
      };
    });
};

/*
 * Explore page market and line chart (formerly PA page)
 * */

const getSelectedRetailerKpiOption = (selectedSaleType, selectedRetailerOption) => {
  switch (selectedRetailerOption) {
    case RetailerKpiOption.share:
      return retailerKpiShareType[selectedSaleType];
    case RetailerKpiOption.mix:
      return retailerKpiMixType[selectedSaleType];
    case RetailerKpiOption.absolute:
    default:
      return retailerKpiAbsoluteType[selectedSaleType];
  }
};

export const processExploreMarketLineChartData = (inputData, selectedSaleType) => {
  const existingNames = new Set();
  const top10Items = limitTop10Items(marketKpiType[selectedSaleType], inputData);

  return inputData.map((item) => {
    const kpiData = item.kpiData?.find((data) => data.kpiType === marketKpiType[selectedSaleType]);

    if (!kpiData || !top10Items.includes(item.brandName)) return null;

    let groupName = `${item.brandName}`;
    while (existingNames.has(groupName)) {
      groupName += ' ';
    }
    existingNames.add(groupName);

    return {
      groupName,
      data: mapChartData(kpiData.marketAverage ?? [], convertStringDateToUnixTimestamp),
      isVisible: groupName !== 'Total',
    };
  });
};

const getSecondKpiData = (selectedSaleType: SaleType, item: { kpiData: any[] }) => {
  if (selectedSaleType === SaleType.revenue) {
    return item?.kpiData.find((data) => data.kpiType === 'My Revenue')?.marketAverage;
  }

  if (selectedSaleType === SaleType.volume) {
    return item?.kpiData.find((data) => data.kpiType === 'My Volume')?.marketAverage;
  }
};

export const processExploreRetailerLineChartData = (inputData, selectedSaleType, selectedRetailer) => {
  const existingNames = new Set();
  const top10Items = limitTop10Items(marketKpiType[selectedSaleType], inputData);
  const kpiKey = getSelectedRetailerKpiOption(selectedSaleType, selectedRetailer);

  return inputData.map((item) => {
    const kpiData = item.kpiData?.find((data) => data.kpiType === kpiKey);
    const secondRespKpiData = getSecondKpiData(selectedSaleType, item);
    if (inputData.length > 10 && !top10Items.includes(item.brandName)) return null;

    let groupName = `${item.brandName}`;
    while (existingNames.has(groupName)) {
      groupName += ' ';
    }
    existingNames.add(groupName);

    return {
      groupName,
      data: mapChartData(kpiData?.marketAverage ?? [], convertStringDateToUnixTimestamp, secondRespKpiData),
      isVisible: groupName !== 'Total',
    };
  });
};

/*
 * Overview page average selling price chart
 * */

export const processOverviewLineChartData = ({ marketAverage, retailerAverage }) =>
  [
    { data: marketAverage, groupName: 'Market ASP' },
    { data: retailerAverage, groupName: 'Retailer ASP' },
  ].map(({ data, groupName }) => ({
    groupName,
    data: mapChartData(data, convertStringDateToUnixTimestamp),
  }));

/**
 *
 * @param periodicity - periodicity from timeframe picker
 * @param currency - selected currency
 * @param kpi - main kpi for the plotted line chart. e.g. absolute revenue, retailer share mix volume
 * @param secondKpi - Second KPI to be shown in tooltips.
 * This is override method for tooltips for Product Compare Retailer Line Chart.
 * This is due to for Retailer absolute revenue and unit, we dont need to show 2 kpi in the tooltips. It will always be the main KPI only, which is Retailer Revenue/Units.
 */
export const createToolTopsPropsForProductRetailerLineChart = (periodicity, currency, kpi, secondKpi) => {
  //For Retailer Absolute Revenue and Units, we dont need to pass second KPI, we need only main KPI in the tooltips.
  if (kpi === secondKpi) {
    return createTooltipProps(periodicity, currency, kpi);
  }
  //Return common tooltips method.
  return createTooltipProps(periodicity, currency, kpi, secondKpi);
};
export const createTooltipProps = (periodicity, currency, kpi, secondKpi = undefined) => {
  let currentPoint = [];
  const processedIndices = new Set();

  return {
    header: (points, xKey) => {
      currentPoint = points;
      processedIndices.clear();
      return formatDate(points[0][xKey], periodicity);
    },
    valueFormat: (val) => {
      const point = currentPoint.find((p, index) => +p.value === +val && !processedIndices.has(index));
      if (point) {
        const index = currentPoint.indexOf(point);
        processedIndices.add(index);

        const primaryKpi = formatKpi(kpi, +val, { currencySymbol: currency });
        const secondaryKpi =
          secondKpi && point.secondKpi !== undefined
            ? ` ${formatKpi(secondKpi, +point.secondKpi, { currencySymbol: currency })}`
            : '';
        return `${primaryKpi}${secondaryKpi}`;
      }
      return formatKpi(kpi, +val, { currencySymbol: currency });
    },
    defaultTopDataPoint: 'Total',
  };
};

export const createAxesConfig = (periodicity, currency, kpi, Ylabel = '') => ({
  xBottom: {
    label: 'Date',
    tickFormat: (date) => formatDate(date, periodicity),
    numTicks: 10,
  },
  yLeft: {
    label: Ylabel,
    hideAxisLine: true,
    tickFormat: (val) => formatKpi(kpi, +val, { currencySymbol: currency }),
    numTicks: 5,
  },
});

export const createChartAxisConfig = {
  [PageType.RETAILER_OVERVIEW]: {
    [ChartKeys[PageType.RETAILER_OVERVIEW].AVG_SELLING_PRICE]: (
      periodicity: any,
      factRevenue: {
        currencySymbol: any;
      },
    ) => ({
      tooltipProps: createTooltipProps(periodicity, factRevenue.currencySymbol, Kpi.averagePrice),
      axesConfig: createAxesConfig(periodicity, factRevenue.currencySymbol, Kpi.averagePrice, 'Price'),
    }),
  },
  [PageType.RI_PRODUCT]: {
    [ChartKeys[PageType.RI_PRODUCT].MARKET]: (periodicity, currency, kpi) => ({
      tooltipProps: createTooltipProps(periodicity, currency, kpi),
      axesConfig: createAxesConfig(periodicity, currency, kpi),
    }),
    [ChartKeys[PageType.RI_PRODUCT].RETAILER]: (periodicity, currency, mainKpi, secondKpi) => ({
      tooltipProps: createToolTopsPropsForProductRetailerLineChart(periodicity, currency, mainKpi, secondKpi),
      axesConfig: createAxesConfig(periodicity, currency, mainKpi),
    }),
  },
  [PageType.EXPLORE]: {
    [ChartKeys[PageType.EXPLORE].MARKET]: (periodicity, currency, kpi) => ({
      tooltipProps: createTooltipProps(periodicity, currency, kpi),
      axesConfig: createAxesConfig(periodicity, currency, kpi),
    }),
    [ChartKeys[PageType.EXPLORE].RETAILER]: (periodicity, currency, mainKpi, secondKpi) => ({
      tooltipProps: createToolTopsPropsForProductRetailerLineChart(periodicity, currency, mainKpi, secondKpi),
      axesConfig: createAxesConfig(periodicity, currency, mainKpi),
    }),
  },
};
